import { memo } from 'react';
import { type ListChildComponentProps } from 'react-window';
import CustomToolTip from '../../CommonComponents/CustomToolTip';
import type { FlatRow, ReportsListItemData, RowResultState } from '../types/reports.types';
import { CheckIconSvg, TriangleSvg } from './ReportsSvgs';
import { cellCls, getTcTmIcon, getReportsGroupTitle } from '../helpers/reports.helpers';
import { CriticalIcon } from '../../Missions/Satellites/TestCase/svgs/TestCaseSvgs';
import { deriveIsCritical } from '../../Missions/Satellites/TestCase/TestCaseHelpers';

// ─── Result cell ──────────────────────────────────────────────────────────────

interface ResultCellProps {
  result: RowResultState;
}

export function ResultCell({ result }: ResultCellProps) {
  if (result === 'pass') {
    return (
      <div className="rp__result-decided rp__result-decided--pass">
        <span>Pass</span>
      </div>
    );
  }
  if (result === 'fail') {
    return (
      <div className="rp__result-decided rp__result-decided--fail">
        <span>Fail</span>
      </div>
    );
  }
  if (result === 'timeout') {
    return (
      <div className="rp__result-decided rp__result-decided--timeout">
        <span>Timeout</span>
      </div>
    );
  }
  return (
    <div className="rp__result-decided">
      <span className="rp__result-dash">-</span>
    </div>
  );
}

// ─── Test-case section ────────────────────────────────────────────────────────

interface SectionProps {
  testPlanResultId?: string;
  testCaseId: string;
  testCaseResultId?: string;
  testPlanName?: string;
  testCaseName?: string;
  hasGroupingMeta: boolean;
  rows: FlatRow[];
  onMarkRow: (rowId: string, result: 'pass' | 'fail' | 'timeout') => void;
  onMarkCase: (testCaseId: string, result: 'pass' | 'fail' | 'timeout') => void;
  selectedRowId: string | null;
  onSelectRow: (row: FlatRow) => void;
}

export function TestCaseSection({
  testCaseId,
  testCaseResultId,
  testPlanName,
  testCaseName,
  hasGroupingMeta,
  rows,
  selectedRowId,
  onSelectRow,
}: SectionProps) {
  const sectionTitle = getReportsGroupTitle({
    testPlanName,
    testCaseName,
    testCaseResultId,
    testCaseId,
  }).toUpperCase();

  const testCaseLabel = testCaseName ?? testCaseResultId ?? testCaseId ?? '—';

  const firstEntry = rows[0]?.entry;
  const rawFailureHandling =
    firstEntry?.failureHandlingMode ??
    (firstEntry?.CommandDetails as any)?.failureHandlingMode;

  const failureHandlingLabel = rawFailureHandling
    ? String(rawFailureHandling).replace(/_/g, ' ').toUpperCase()
    : null;

  const isSkipAndContinue = rawFailureHandling === 'SKIP_AND_CONTINUE';
  const isAbortOnFailure =
    rawFailureHandling &&
    (String(rawFailureHandling).toUpperCase() === 'ABORT_ON_FAILURE' ||
      String(rawFailureHandling).toUpperCase() === 'ABORT');
  const displayLabel = isSkipAndContinue ? 'PARTIALLY EXECUTED' : failureHandlingLabel;
  const hasPassedEntry = rows.some((row) => row.result === 'pass');
  const hasFailedEntry = rows.some((row) => row.result === 'fail');
  const showBadge = Boolean(displayLabel && hasPassedEntry && hasFailedEntry);

  const customStyle = isSkipAndContinue
    ? {
        background: 'rgba(245, 158, 11, 0.15)',
        border: '0.5px solid rgba(245, 158, 11, 0.35)',
        color: '#fcd34d',
      }
    : isAbortOnFailure
    ? {
        background: 'rgba(255, 51, 51, 0.15)',
        border: '0.5px solid rgba(255, 51, 51, 0.35)',
        color: '#fca5a5',
      }
    : undefined;

  return (
    <div className="rp__section">
      {hasGroupingMeta && (
        <div className="rp__section-header">
          <div className="rp__section-header__left rp__section-header__left--static" aria-hidden>
            <CustomToolTip title={sectionTitle} placement="top">
              <span className="rp__section-header__name">
                {testPlanName ? (
                  <>
                    <span className="test-plan-text">{testPlanName}</span>
                    <span className="test-case-text">|</span>
                    <span className="test-case-text">{testCaseLabel}</span>
                  </>
                ) : (
                  <span className="test-case-text">{testCaseLabel}</span>
                )}
                {showBadge && displayLabel && (
                  <span className="rp-failure-handling-badge" style={customStyle}>
                    {displayLabel}
                  </span>
                )}
              </span>
            </CustomToolTip>
          </div>
        </div>
      )}

      <div className={`rp__rows-container${!hasGroupingMeta ? ' rp__rows-container--ungrouped' : ''}`}>
        {rows.map((row) => {
          const isActive = selectedRowId === row.uniqueEntryId;
          const isRowCritical = Boolean(row.entry?.isCritical || (row.entry && deriveIsCritical(row.entry)));

          return (
            <div
              key={row.uniqueEntryId}
              role="button"
              className={`rp__row${isActive ? ' rp__row--active' : ''}`}
              onClick={() => onSelectRow(row)}
              data-testid={`reports-row-${row.uniqueEntryId}`}
            >
              <div className={`rp__cell rp__cell--dir${isActive ? ' rp__cell--active-border' : ''}`}>
                <CustomToolTip title={row.tctmType || (row.direction === 'down' ? 'TM' : 'TC')} placement="top">
                  <span className="rp__cell-icon-wrap">{getTcTmIcon(row.tctmType, row.direction)}</span>
                </CustomToolTip>
                {isRowCritical && (
                  <CustomToolTip title="Critical Command" placement="top">
                    <span className="tcd-critical-icon-cell">
                      <CriticalIcon />
                    </span>
                  </CustomToolTip>
                )}
              </div>

              <div className={cellCls('time', isActive)}>
                <span className="rp__cell-text">{row.timestamp ?? '—'}</span>
              </div>

              <div className={cellCls('id', isActive)}>
                <span className="rp__cell-text">{row.tcTmId ?? row.operationId}</span>
              </div>

              <div className={cellCls('flex', isActive)}>
                <span className="rp__cell-text">{row.source ?? row.commandName}</span>
              </div>

              <div className={cellCls('flex', isActive)}>
                <span className="rp__cell-text">{row.destination ?? '—'}</span>
              </div>

              <div className={cellCls('result', isActive)} data-testid={`reports-result-${row.uniqueEntryId}`}>
                <ResultCell result={row.result} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Virtualised section wrapper ──────────────────────────────────────────────

export const VirtualSection = memo<ListChildComponentProps<ReportsListItemData>>(
  ({ index, style, data }) => {
    const group = data.groups[index];
    if (!group) return null;

    return (
      <div style={style}>
        <div className="rp__virtual-item">
          <TestCaseSection
            testPlanResultId={group.testPlanResultId}
            testCaseId={group.testCaseId}
            testCaseResultId={group.testCaseResultId}
            testPlanName={group.testPlanName}
            testCaseName={group.testCaseName}
            hasGroupingMeta={group.hasGroupingMeta}
            rows={group.rows}
            onMarkRow={data.onMarkRow}
            onMarkCase={data.onMarkCase}
            selectedRowId={data.selectedRowId}
            onSelectRow={data.onSelectRow}
          />
        </div>
      </div>
    );
  },
);

VirtualSection.displayName = 'ReportsVirtualSection';
